import { ChevronRight, Computer, LogOut, Moon, Sun } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { T } from "@/i18n";
import { auth } from "@/lib/auth";
import { Worker } from "@/models";
import { useMemo } from "react";
import { Theme, useTheme } from "./theme-provider";

export function NavUser({ user }: { user: Worker | null | undefined }) {
  const { isMobile } = useSidebar();

  const { setTheme } = useTheme();

  const themes: Array<{ label: string; value: Theme; icon: JSX.Element }> = [
    {
      label: "nav.user.themes.light",
      value: "light",
      icon: <Sun className="text-yellow-400" />,
    },
    {
      label: "nav.user.themes.dark",
      value: "dark",
      icon: <Moon />,
    },
    {
      label: "nav.user.themes.system",
      value: "system",
      icon: <Computer className="text-muted-foreground" />,
    },
  ];

  const initials = useMemo(() => {
    if (!user) return undefined;

    const [first, last] = user.name.split(" ");

    const firstChar = first.charAt(0);
    const lastChar = last ? last.charAt(0) : firstChar;

    return `${firstChar}${lastChar}`.toUpperCase();
  }, [user]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage alt={user?.name} />
                <AvatarFallback className="rounded-lg">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user?.name}</span>
                <span className="truncate text-xs">{user?.email}</span>
              </div>
              <ChevronRight className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage alt={user?.name} />
                  <AvatarFallback className="rounded-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user?.name}</span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel>
                <T tKey="nav.user.themes.title" />
              </DropdownMenuLabel>
              {themes.map(({ icon, label, value }) => (
                <DropdownMenuItem
                  key={value}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    setTheme(value);
                  }}
                >
                  {icon}
                  <T tKey={label} />
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => auth.logout()}>
              <LogOut />
              <T tKey="common.logout" />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
